import { format, formatDistanceToNowStrict } from 'date-fns';
import { de, enUS, pl, cs } from 'date-fns/locale';
import i18n from 'locale';

const getLocaleSet = (lng: string) => {
	switch (lng) {
		case 'cs':
			return cs;
		case 'de':
			return de;
		case 'en':
			return enUS;
		case 'pl':
		default:
			return pl;
	}
};

const getLocale = (lng: string) => {
	switch (lng) {
		case 'cs':
			return 'cs';
		case 'de':
			return 'de-DE';
		case 'en':
			return 'en-US';
		case 'pl':
		default:
			return 'pl-PL';
	}
};

export type DateFormatType =
	'full' | 'hours' | 'dayAndMonth' | 'dayMonth' | 'namedMonth' | 'date' | 'time' | 'dayAndMonthPlusYear';

export const dateFormat = (_date: Date | string, type: DateFormatType) => {
	try {
		const date = _date instanceof Date ? _date : new Date(_date);

		switch (type) {
			case 'full':
				return format(date, 'Pp', { locale: getLocaleSet(i18n.language) });
			case 'hours':
				return format(date, 'HH:mm', { locale: getLocaleSet(i18n.language) });
			case 'dayAndMonth':
				return format(date, 'd MMMM', { locale: getLocaleSet(i18n.language) });
			case 'dayMonth':
				return format(date, 'dd.MM', { locale: getLocaleSet(i18n.language) });
			case 'dayAndMonthPlusYear':
				return format(date, 'd MMMM yyyy', { locale: getLocaleSet(i18n.language) });
			case 'namedMonth':
				return format(date, 'd MMMM yyyy, HH:mm', { locale: getLocaleSet(i18n.language) });
			case 'date':
				return date.toLocaleDateString(getLocale(i18n.language));
			case 'time':
				return date.toLocaleTimeString(getLocale(i18n.language));
			default:
				return date.toLocaleString(getLocale(i18n.language));
		}
	} catch {
		console.error('invalid date', _date);
		return undefined;
	}
};

export const formatDistanceToNow = (
	date: Date,
	options?: {
		addSuffix?: boolean;
		unit?: 'second' | 'minute' | 'hour' | 'day' | 'month' | 'year';
		roundingMethod?: 'floor' | 'ceil' | 'round';
	}
) => {
	return formatDistanceToNowStrict(date, {
		locale: getLocaleSet(i18n.language),
		...options,
	});
};

export type RelativeCountdownType = 'now' | 'soon' | 'distance';

type RelativeCountdownOptions = {
	start: Date | string;
	end?: Date | string | null;
	maxFutureMs?: number;
	soonMs?: number;
};

type RelativeCountdownResult = {
	highlight: boolean;
	type: RelativeCountdownType | null;
	distance?: string;
};

const normalizeDate = (date?: Date | string | null) => {
	if (!date) {
		return null;
	}

	return date instanceof Date ? date : new Date(date);
};

export const getRelativeCountdown = ({
	start,
	end,
	maxFutureMs = 7 * 24 * 60 * 60 * 1000,
	soonMs = 60 * 60 * 1000,
}: RelativeCountdownOptions): RelativeCountdownResult => {
	const startDate = normalizeDate(start);
	const endDate = normalizeDate(end);

	if (!startDate || Number.isNaN(startDate.getTime()) || (endDate && Number.isNaN(endDate.getTime()))) {
		return { highlight: false, type: null };
	}

	const now = new Date();
	const msUntilStart = startDate.getTime() - now.getTime();
	const inProgress = Boolean(endDate && now >= startDate && now <= endDate);

	if (inProgress) {
		return { highlight: true, type: 'now' };
	}

	if (msUntilStart > 0 && msUntilStart < soonMs) {
		return { highlight: true, type: 'soon' };
	}

	if (msUntilStart > 0 && msUntilStart < maxFutureMs) {
		return {
			highlight: true,
			type: 'distance',
			distance: formatDistanceToNow(startDate, {
				addSuffix: true,
				roundingMethod: 'floor',
			}),
		};
	}

	return { highlight: false, type: null };
};
